import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import { useLocalizedText } from '../../hooks/localizedText';
import './richTextEditor.scss';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  readOnly?: boolean;
  hasError?: boolean;
  testId?: string;
}

const HIGHLIGHT_COLORS = {
  semantic: [
    { color: '#fdf3f1', label: 'Error' },
    { color: '#fffef0', label: 'Warning' },
    { color: '#f2f8f0', label: 'Success' },
    { color: '#f1faff', label: 'Info' },
    { color: '#eaeded', label: 'Neutral' },
  ],
  standard: [
    { color: '#fef3cd', label: 'Yellow' },
    { color: '#d4edda', label: 'Green' },
    { color: '#cce5ff', label: 'Blue' },
    { color: '#f8d7da', label: 'Pink' },
    { color: '#ffe5cc', label: 'Orange' },
  ],
};

export function RichTextEditor({
  value,
  onChange,
  onBlur,
  placeholder,
  readOnly = false,
  hasError = false,
  testId,
}: RichTextEditorProps) {
  const { t } = useLocalizedText(['richTextEditor']);
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  const [showLinkInput, setShowLinkInput] = useState(false);
  const showLinkInputRef = useRef(false);
  const [linkUrl, setLinkUrl] = useState('');
  const linkInputRef = useRef<HTMLInputElement>(null);

  const [showHighlightPicker, setShowHighlightPicker] = useState(false);
  const [showHeadingMenu, setShowHeadingMenu] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4] },
        blockquote: {},
        strike: {},
        codeBlock: false,
        code: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        linkOnPaste: true,
        defaultProtocol: 'https',
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: value,
    editable: !readOnly,
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
    onBlur: () => {
      onBlurRef.current?.();
    },
    onSelectionUpdate: ({ editor: e }) => {
      if (e.isActive('link')) {
        setLinkUrl(e.getAttributes('link').href ?? '');
        setShowLinkInput(true);
      } else if (showLinkInputRef.current) {
        setShowLinkInput(false);
      }
    },
  });

  useEffect(() => {
    editor?.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    showLinkInputRef.current = showLinkInput;
  }, [showLinkInput]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      const { from, to } = editor.state.selection;
      editor.commands.setContent(value, false);
      const maxPos = editor.state.doc.content.size;
      editor.commands.setTextSelection({ from: Math.min(from, maxPos), to: Math.min(to, maxPos) });
    }
  }, [value, editor]);

  // --- Shared helpers ---
  const normalizeUrl = (url: string): string => {
    const trimmed = url.trim();
    return trimmed.startsWith('http://') || trimmed.startsWith('https://')
      ? trimmed
      : `https://${trimmed}`;
  };

  const closeAllPopovers = () => {
    setShowLinkInput(false);
    setShowHighlightPicker(false);
    setShowHeadingMenu(false);
  };

  // --- Link ---
  const openLinkInput = useCallback(() => {
    if (showLinkInput) {
      setShowLinkInput(false);
      editor?.commands.focus();
      return;
    }
    closeAllPopovers();
    const existing = editor?.getAttributes('link').href ?? '';
    setLinkUrl(existing);
    setShowLinkInput(true);
    setTimeout(() => linkInputRef.current?.focus(), 0);
  }, [editor, showLinkInput]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    if (linkUrl.trim()) {
      const href = normalizeUrl(linkUrl);
      editor.chain().focus().extendMarkRange('link').setLink({ href, target: '_blank' }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setShowLinkInput(false);
  }, [editor, linkUrl]);

  const cancelLink = useCallback(() => {
    setShowLinkInput(false);
    editor?.commands.focus();
  }, [editor]);

  // --- Highlight ---
  const toggleHighlightPicker = useCallback(() => {
    if (showHighlightPicker) {
      setShowHighlightPicker(false);
      return;
    }
    closeAllPopovers();
    setShowHighlightPicker(true);
  }, [showHighlightPicker]);

  // --- Heading ---
  const toggleHeadingMenu = useCallback(() => {
    if (showHeadingMenu) {
      setShowHeadingMenu(false);
      return;
    }
    closeAllPopovers();
    setShowHeadingMenu(true);
  }, [showHeadingMenu]);

  const applyHighlight = useCallback(
    (color: string) => {
      if (!editor) return;
      editor.chain().focus().toggleHighlight({ color }).run();
      setShowHighlightPicker(false);
    },
    [editor]
  );

  if (!editor) return null;

  const classNames = [
    'rich-text-editor',
    hasError && 'rich-text-editor--error',
    readOnly && 'rich-text-editor--readonly',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classNames} data-testid={testId}>
      {!readOnly && (
        <>
          <Toolbar
            editor={editor}
            t={t}
            onLinkClick={openLinkInput}
            showLinkActive={showLinkInput}
            onHighlightClick={toggleHighlightPicker}
            showHighlightActive={showHighlightPicker}
            showHeadingMenu={showHeadingMenu}
            onHeadingClick={toggleHeadingMenu}
          />

          {showHighlightPicker && (
            <div className="rich-text-editor__color-picker">
              <div className="rich-text-editor__color-row">
                {HIGHLIGHT_COLORS.semantic.map((c) => (
                  <button
                    key={c.color}
                    type="button"
                    className="rich-text-editor__color-swatch"
                    style={{ backgroundColor: c.color }}
                    onClick={() => applyHighlight(c.color)}
                    aria-label={c.label}
                    title={c.label}
                  />
                ))}
              </div>
              <div className="rich-text-editor__color-row">
                {HIGHLIGHT_COLORS.standard.map((c) => (
                  <button
                    key={c.color}
                    type="button"
                    className="rich-text-editor__color-swatch"
                    style={{ backgroundColor: c.color }}
                    onClick={() => applyHighlight(c.color)}
                    aria-label={c.label}
                    title={c.label}
                  />
                ))}
              </div>
              {editor.isActive('highlight') && (
                <button
                  type="button"
                  className="rich-text-editor__color-clear"
                  onClick={() => {
                    editor.chain().focus().unsetHighlight().run();
                    setShowHighlightPicker(false);
                  }}
                >
                  {t('toolbar.clearHighlight')}
                </button>
              )}
            </div>
          )}

          {showLinkInput && (
            <div className="rich-text-editor__link-input-bar">
              <input
                ref={linkInputRef}
                className="rich-text-editor__bubble-input"
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applyLink();
                  if (e.key === 'Escape') cancelLink();
                }}
                placeholder="https://"
                aria-label={t('link.urlInput')}
              />
              <button
                type="button"
                className="rich-text-editor__bubble-action"
                onClick={applyLink}
                aria-label={t('link.apply')}
                title={t('link.apply')}
              >
                <CheckIcon />
              </button>
              <button
                type="button"
                className="rich-text-editor__bubble-action"
                onClick={() => {
                  if (linkUrl.trim())
                    window.open(normalizeUrl(linkUrl), '_blank', 'noopener,noreferrer');
                }}
                disabled={!linkUrl.trim()}
                aria-label={t('link.openInNewWindow')}
                title={t('link.openInNewWindow')}
              >
                <ExternalLinkIcon />
              </button>
              {editor.isActive('link') && (
                <button
                  type="button"
                  className="rich-text-editor__bubble-action"
                  onClick={() => {
                    editor.chain().focus().extendMarkRange('link').unsetLink().run();
                    setShowLinkInput(false);
                  }}
                  aria-label={t('link.remove')}
                  title={t('link.remove')}
                >
                  <TrashIcon />
                </button>
              )}
              <button
                type="button"
                className="rich-text-editor__bubble-action"
                onClick={cancelLink}
                aria-label={t('link.cancel')}
                title={t('link.cancel')}
              >
                <CloseIcon />
              </button>
            </div>
          )}
        </>
      )}
      <div className="rich-text-editor__content">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Toolbar                                                           */
/* ------------------------------------------------------------------ */

function Toolbar({
  editor,
  t,
  onLinkClick,
  showLinkActive,
  onHighlightClick,
  showHighlightActive,
  showHeadingMenu,
  onHeadingClick,
}: {
  editor: NonNullable<ReturnType<typeof useEditor>>;
  t: (key: string) => string;
  onLinkClick: () => void;
  showLinkActive: boolean;
  onHighlightClick: () => void;
  showHighlightActive: boolean;
  showHeadingMenu: boolean;
  onHeadingClick: () => void;
}) {
  const activeHeading = [1, 2, 3, 4].find((level) => editor.isActive('heading', { level }));

  return (
    <div
      className="rich-text-editor__toolbar"
      role="toolbar"
      aria-label={t('toolbar.textFormatting')}
    >
      {/* Heading dropdown */}
      <div className="rich-text-editor__toolbar-dropdown-wrapper">
        <ToolbarButton
          icon={activeHeading ? null : <HeadingIcon />}
          ariaLabel={t('toolbar.heading')}
          isActive={!!activeHeading}
          onClick={() => onHeadingClick()}
          label={activeHeading ? `H${activeHeading}` : undefined}
          ariaHasPopup
          ariaExpanded={showHeadingMenu}
        />
        {showHeadingMenu && (
          <div className="rich-text-editor__toolbar-dropdown">
            {([1, 2, 3, 4] as const).map((level) => (
              <button
                key={level}
                type="button"
                className={`rich-text-editor__toolbar-dropdown-item${editor.isActive('heading', { level }) ? ' active' : ''}`}
                onClick={() => {
                  editor.chain().focus().toggleHeading({ level }).run();
                  onHeadingClick();
                }}
              >
                H{level}
              </button>
            ))}
            <button
              type="button"
              className={`rich-text-editor__toolbar-dropdown-item${!activeHeading ? ' active' : ''}`}
              onClick={() => {
                editor.chain().focus().setParagraph().run();
                onHeadingClick();
              }}
            >
              {t('toolbar.paragraph')}
            </button>
          </div>
        )}
      </div>

      <div className="rich-text-editor__toolbar-divider" />

      <ToolbarButton
        icon={<BoldIcon />}
        ariaLabel={t('toolbar.bold')}
        isActive={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        icon={<ItalicIcon />}
        ariaLabel={t('toolbar.italic')}
        isActive={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        icon={<UnderlineIcon />}
        ariaLabel={t('toolbar.underline')}
        isActive={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      <ToolbarButton
        icon={<StrikethroughIcon />}
        ariaLabel={t('toolbar.strikethrough')}
        isActive={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />
      <ToolbarButton
        icon={<HighlightIcon />}
        ariaLabel={t('toolbar.highlight')}
        isActive={editor.isActive('highlight') || showHighlightActive}
        onClick={onHighlightClick}
        ariaHasPopup
        ariaExpanded={showHighlightActive}
      />

      <div className="rich-text-editor__toolbar-divider" />

      <ToolbarButton
        icon={<BulletListIcon />}
        ariaLabel={t('toolbar.bulletedList')}
        isActive={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />
      <ToolbarButton
        icon={<OrderedListIcon />}
        ariaLabel={t('toolbar.numberedList')}
        isActive={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <ToolbarButton
        icon={<BlockquoteIcon />}
        ariaLabel={t('toolbar.blockquote')}
        isActive={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      />

      <div className="rich-text-editor__toolbar-divider" />

      <ToolbarButton
        icon={<LinkIcon />}
        ariaLabel={t('toolbar.link')}
        isActive={editor.isActive('link') || showLinkActive}
        onClick={onLinkClick}
      />

      <div className="rich-text-editor__toolbar-divider" />

      <ToolbarButton
        icon={<UndoIcon />}
        ariaLabel={t('toolbar.undo')}
        isActive={false}
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      />
      <ToolbarButton
        icon={<RedoIcon />}
        ariaLabel={t('toolbar.redo')}
        isActive={false}
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      />
    </div>
  );
}

function ToolbarButton({
  icon,
  ariaLabel,
  isActive,
  onClick,
  disabled,
  label,
  ariaHasPopup,
  ariaExpanded,
}: {
  icon: React.ReactNode;
  ariaLabel: string;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
  label?: string;
  ariaHasPopup?: boolean;
  ariaExpanded?: boolean;
}) {
  return (
    <button
      type="button"
      className={`rich-text-editor__toolbar-button${isActive ? ' active' : ''}`}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-pressed={ariaHasPopup ? undefined : isActive}
      aria-haspopup={ariaHasPopup || undefined}
      aria-expanded={ariaHasPopup ? ariaExpanded : undefined}
      title={ariaLabel}
      disabled={disabled}
    >
      {icon}
      {label && <span className="rich-text-editor__toolbar-button-label">{label}</span>}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  SVG Icons                                                         */
/* ------------------------------------------------------------------ */

const svgProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const HeadingIcon = () => (
  <svg {...svgProps}>
    <path d="M6 4v16" />
    <path d="M18 4v16" />
    <path d="M6 12h12" />
  </svg>
);
const BoldIcon = () => (
  <svg {...svgProps}>
    <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
  </svg>
);
const ItalicIcon = () => (
  <svg {...svgProps}>
    <line x1="19" y1="4" x2="10" y2="4" />
    <line x1="14" y1="20" x2="5" y2="20" />
    <line x1="15" y1="4" x2="9" y2="20" />
  </svg>
);
const UnderlineIcon = () => (
  <svg {...svgProps}>
    <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
    <line x1="4" y1="21" x2="20" y2="21" />
  </svg>
);
const StrikethroughIcon = () => (
  <svg {...svgProps}>
    <path d="M16 4H9a3 3 0 0 0-3 3c0 2 1.5 3 4 3.5" />
    <path d="M14 16.5c2.5.5 4 1.5 4 3.5a3 3 0 0 1-3 3H8" />
    <line x1="4" y1="12" x2="20" y2="12" />
  </svg>
);
const HighlightIcon = () => (
  <svg {...svgProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    <line x1="2" y1="22" x2="6" y2="22" strokeWidth="3" />
  </svg>
);
const BulletListIcon = () => (
  <svg {...svgProps}>
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <circle cx="4" cy="6" r="1" fill="currentColor" />
    <circle cx="4" cy="12" r="1" fill="currentColor" />
    <circle cx="4" cy="18" r="1" fill="currentColor" />
  </svg>
);
const OrderedListIcon = () => (
  <svg {...svgProps}>
    <line x1="10" y1="6" x2="21" y2="6" />
    <line x1="10" y1="12" x2="21" y2="12" />
    <line x1="10" y1="18" x2="21" y2="18" />
    <text x="2" y="8" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">
      1
    </text>
    <text x="2" y="14" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">
      2
    </text>
    <text x="2" y="20" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">
      3
    </text>
  </svg>
);
const BlockquoteIcon = () => (
  <svg {...svgProps}>
    <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" />
    <path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z" />
  </svg>
);
const LinkIcon = () => (
  <svg {...svgProps}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);
const CheckIcon = () => (
  <svg {...svgProps}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const CloseIcon = () => (
  <svg {...svgProps}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const UndoIcon = () => (
  <svg {...svgProps}>
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
);
const RedoIcon = () => (
  <svg {...svgProps}>
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);
const ExternalLinkIcon = () => (
  <svg {...svgProps}>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);
const TrashIcon = () => (
  <svg {...svgProps}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
